/**
 * Navbar (V2) — Phase 11.5.7 LITERAL clone of the reference `navbar/navbar.tsx`. ONE
 * component per kind, API unchanged (`SectionComponentProps<'navbar'>`). The DOM structure
 * and every CSS value mirror the reference navbar's LANDING chrome exactly (see navbar.css).
 *
 * The reference navbar is heavily app-coupled (search field, cart, user/profile dropdown,
 * country selector, "more" dropdown, language toggle, foodi/fashion template branches, app
 * routing + next-intl + init/user/navigation contexts). Per the phase rule ONLY the rendered
 * landing chrome the Builder has data for is ported:
 *   - nav links       ← reference `NavbarMenu`  (`.navbar-list` / `.navbar-list-item`)  [items]
 *   - primary action button ← reference landing order-now button (`.navbar-container-landing > .button.button-secondary`)  [actions]
 *   - hamburger + menu← reference `NavbarSlideMneuButton` + a LOCAL-state slide-down drawer
 *                       (Builder V2 substitute for the app's `navContext.toggleSidenav` sidenav)
 * Everything else listed above is EXCLUDED (no production contexts/routing/providers exist).
 *
 * The Header no longer renders a logo (simplification: the Header is only customizable via
 * its navigation links — see NavbarEditorPanel.tsx) and no longer offers a layout choice —
 * it always renders the single `standard` arrangement (registerBuiltins.ts no longer lists
 * `minimal`/`centered` as selectable; they remain valid `NavbarLayout` schema values for
 * backward compatibility, but nothing produces them anymore and there is no `--minimal`
 * styling left to select — see navbar.css).
 *
 * Root is a `<header>` carrying `sbp-scope` so the reference `--color-*`/`--padding-*`/… tokens
 * (foundation.css) resolve and the reference base reset applies — without the `.sbp-section`
 * 100px band. `themeVars`/`data-ds` are kept for parity with the other sections; the navbar's
 * visual uses the literal reference tokens, not the theme's `--sb-*` design values. The bar's
 * background color (`--color-primary`) is driven by `theme.branding.brandColor` through the
 * normal theme pipeline (resolveTheme → themeVars) — unaffected by this simplification, with
 * the existing fallback applying whenever brandColor is missing.
 */
import { useEffect, useRef, useState } from 'react'
import type { SectionComponentProps } from '../../runtime/types'
import { Container, Actions, Icon, cx } from '../shared'
import { themeVars } from '../shared/theme/themeVars'
import './navbar.css'

export function Navbar(p: SectionComponentProps<'navbar'>) {
  const { content, id, theme, locale } = p

  const [open, setOpen] = useState(false)
  const navRef = useRef<HTMLElement | null>(null)

  // Close the mobile drawer on outside click (ported from the reference more-menu pattern).
  useEffect(() => {
    if (!open) return
    function onDown(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const links = content.items ?? []
  const menuId = `${id}-menu`

  return (
    <header
      ref={navRef}
      className={cx('sbp-scope', 'sbp-navbar')}
      style={themeVars(theme)}
      data-ds={theme.designSystem}
      dir={locale === 'ar' ? 'rtl' : undefined}
      aria-label="Primary"
    >
      {/* reference .navbar-container (over .sbp-container: max-width / margin / padding-inline) */}
      <Container className="sbp-navbar__container">
        {/* reference NavbarSlideMneuButton (.navbar-btn-menu) — mobile/tablet only */}
        <button
          type="button"
          className="sbp-navbar__menu-btn"
          aria-expanded={open}
          aria-controls={menuId}
          aria-label="Toggle menu"
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name={open ? 'x' : 'menu'} size={24} />
        </button>

        {/* reference NavbarLandingContent (.navbar-container-landing) */}
        <div className="sbp-navbar__landing">
          {/* reference NavbarMenu (.navbar-list) — labels upper-cased in JS, as the reference does */}
          <ul id={menuId} className={cx('sbp-navbar__list', open && 'sbp-navbar__list--open')}>
            {links.map((link) => (
              <li key={link.id}>
                <a className="sbp-navbar__link" href={link.value ?? '#'} onClick={() => setOpen(false)}>
                  <span>{link.title.toUpperCase()}</span>
                </a>
              </li>
            ))}
          </ul>
          {/* reference landing order-now button (.button.button-secondary) via shared Actions */}
          {content.actions && content.actions.length > 0 ? (
            <div className="sbp-navbar__cta">
              <Actions actions={content.actions} />
            </div>
          ) : null}
        </div>
      </Container>
    </header>
  )
}
